/**
 * Блок «Ввод объектов нежилого назначения» (US-23 FR-3) — таблица из трёх
 * колонок: наименование вида объекта, количество объектов, фактически
 * введённая мощность.
 *
 * Три правила, отличающие блок от остальных таблиц «Аналитики»:
 *  - **Колонки «Доля, %» нет** (FR-3.1): база сравнения для неё не
 *    определена, а величины строк несопоставимы между собой.
 *  - **Единица измерения своя у каждой строки** (FR-3.3) — кв. м, кв. м
 *    торговой площади, единицы, посадочные места, головы, тонны в год,
 *    километры — и приходит внутри величины, а не в шапке колонки.
 *  - **Итоговой строки нет** (FR-3.5): складывать колонку нельзя. Общее
 *    количество объектов показывает плитка плашки, и берётся оно из
 *    итоговой строки выпуска, а не суммой строк (US-24 FR-6.4).
 *
 * Состав строк задаёт выпуск региона (FR-3.2): фиксированного перечня из
 * двенадцати видов DP-384 нет, вид без значения в таблицу не попадает, а
 * вид с закрытой величиной («х») выводится как «нет данных» — не
 * «конфиденциально» (DP-518; сводит разбор ответа,
 * `normalizeConstructionNonresidentialUs24`). Порядок строк — порядок
 * ответа (US-24 FR-7.2).
 *
 * Отчётный период блока — квартал, он отстаёт от накопленного периода
 * плашки, и это нормальное состояние источника (FR-3.6, FR-5.2).
 */
import { FileSpreadsheet, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PeriodCaption,
  SourceNote,
  StatNoData,
  StatNumber,
  fmt,
  fmt1,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type {
  ConstructionNonresidentialResponse,
  ConstructionNonresidentialRow,
} from '../../../types/analytics';

interface Props {
  data: ConstructionNonresidentialResponse | null;
  loading: boolean;
}

export const ConstructionNonresidential = ({ data, loading }: Props) => {
  const { t } = useTranslation();
  // Drill-through до строки территории внутри вида объекта (FR-8.3);
  // глубже экран не идёт — источник таких данных не публикует.
  const [drillRow, setDrillRow] =
    useState<ConstructionNonresidentialRow | null>(null);

  const rows = data?.rows ?? [];

  return (
    <Card
      className="h-full shadow-card"
      data-testid="analytics-construction-nonres"
    >
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-x-4 gap-y-1 space-y-0">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* Знак блока в зелёном квадрате — шапка карточки по макету. */}
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600"
            aria-hidden="true"
          >
            <FileSpreadsheet className="size-4" strokeWidth={1.8} />
          </span>
          <CardTitle className="min-w-0 text-base text-text">
            {t('analytics.construction.nonresidential.title')}
          </CardTitle>
        </div>
        {/* Квартальный период блока — свой, он отстаёт от плашки (FR-5.2). */}
        <PeriodCaption
          period={data?.period ?? null}
          testId="construction-nonres-period"
        />
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : rows.length === 0 ? (
          <StatNoData text={t('common.noData')} />
        ) : (
          <>
            <section data-testid="block-construction-nonresidential">
              <Table>
                <TableHeader>
                  <TableRow className="border-surface-border bg-surface-muted/60 hover:bg-surface-muted/60">
                    <TableHead className="h-auto rounded-l-lg py-2 text-text-faint">
                      {t('analytics.construction.nonresidential.columnName')}
                    </TableHead>
                    <TableHead className="h-auto whitespace-nowrap py-2 text-right text-text-faint">
                      {t('analytics.construction.nonresidential.columnCount')}
                    </TableHead>
                    <TableHead className="h-auto whitespace-nowrap rounded-r-lg py-2 text-right text-text-faint">
                      {t('analytics.construction.nonresidential.columnActual')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => {
                    const drillable = row.territories.length > 0;
                    const key = row.code ?? row.name ?? String(index);
                    return (
                      <TableRow
                        key={key}
                        data-testid={`construction-nonres-row-${key}`}
                        className={cn(
                          // Чередование строк — как на макете; на hover
                          // подсветка раскрываемой строки его перекрывает.
                          'border-surface-border even:bg-surface-page/60',
                          drillable
                            ? 'cursor-pointer hover:bg-brand-50/60'
                            : 'hover:bg-transparent even:hover:bg-surface-page/60',
                        )}
                        // Строка без первичных данных неактивна: клик не
                        // выполняется, ошибка не показывается (FR-8.2).
                        tabIndex={drillable ? 0 : undefined}
                        role={drillable ? 'button' : undefined}
                        onClick={drillable ? () => setDrillRow(row) : undefined}
                        onKeyDown={
                          drillable
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setDrillRow(row);
                                }
                              }
                            : undefined
                        }
                      >
                        <TableCell className="py-3">
                          {/* Сокращённая формулировка дешборда, полное
                              наименование источника — подсказкой (FR-3.4). */}
                          <span
                            className="block min-w-0 font-semibold leading-snug"
                            title={row.full_name ?? undefined}
                          >
                            {row.name}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <StatNumber
                            value={row.count}
                            format={fmt}
                            testId={`construction-nonres-count-${key}`}
                          />
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          {/* Единица — своя у строки и из ответа (FR-3.3). */}
                          <StatNumber
                            value={row.actual}
                            format={fmt1}
                            withUnit
                            testId={`construction-nonres-actual-${key}`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </section>

            {/* Сноска о единицах и раскрытии строки (макет): строка со
                знаком «i» под таблицей. */}
            <p className="mt-3 flex items-start gap-1.5 text-xs leading-snug text-text-faint">
              <Info
                className="mt-px size-3.5 shrink-0"
                strokeWidth={1.8}
                aria-hidden="true"
              />
              <span className="min-w-0">
                {t('analytics.construction.nonresidential.note')}
              </span>
            </p>
            <SourceNote source={data?.source ?? null} />
          </>
        )}
      </CardContent>

      {/* Территории внутри вида объекта (FR-8.3): районные строки часто
          закрыты «х» при раскрытом итоге по области (FR-8.4) и, как и
          сам вид, показываются «нет данных» (DP-518). */}
      <Dialog
        open={drillRow !== null}
        onOpenChange={(open) => {
          if (!open) setDrillRow(null);
        }}
      >
        <DialogContent
          className="max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto"
          data-testid="construction-nonres-drill"
        >
          {drillRow !== null && (
            <>
              <DialogHeader>
                <DialogTitle className="text-left text-base leading-snug">
                  {drillRow.full_name ?? drillRow.name}
                </DialogTitle>
                <p className="text-sm text-text-muted">
                  {t('analytics.construction.nonresidential.drillHint')}
                </p>
              </DialogHeader>
              <ul>
                {drillRow.territories.map((territory, index) => (
                  <li
                    key={territory.kato ?? territory.name ?? index}
                    className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-dashed border-surface-border py-2 text-sm last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 leading-snug text-text-muted">
                      {territory.name ?? '—'}
                    </span>
                    <StatNumber
                      value={territory.count}
                      format={fmt}
                      className="shrink-0 text-right"
                    />
                    <StatNumber
                      value={territory.actual}
                      format={fmt1}
                      withUnit
                      className="shrink-0 text-right"
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
